<template>
  <div class="max-w-7xl mx-auto px-8 py-12 text-gray-800">
    <!-- Header Section -->
    <div class="text-center mb-12 relative pb-6">
      <h1 class="text-4xl font-bold mb-2">About Me</h1>
      <div class="text-lg text-gray-600">Get to know me better</div>
      <div
        class="absolute bottom-0 left-1/2 transform -translate-x-1/2 w-24 h-1 bg-gradient-to-r from-blue-600 to-blue-300">
      </div>
    </div>

    <!-- Bio Section -->
    <div class="bg-white rounded-lg shadow-lg p-8 mb-12">
      <div class="flex flex-col md:flex-row gap-8 items-center">
        <div class="flex-1">
          <h2 class="text-3xl font-bold mb-4 relative pb-2">
            Who I Am
            <span class="absolute bottom-0 left-0 w-3/5 h-1 bg-blue-600"></span>
          </h2>
          <p class="text-gray-700">
            [Your brief introduction goes here. Share a bit about yourself, your background,
            and what drives you professionally and personally.]
          </p>
        </div>
        <div class="flex-1 flex justify-center">
          <img :src="profileImage" alt="Profile Picture" class="w-72 h-72 object-cover rounded-lg shadow-md" />
        </div>
      </div>
    </div>

    <!-- Hobbies Section -->
    <div class="bg-white rounded-lg shadow-lg p-8 mb-12">
      <h2 class="text-3xl font-bold mb-8 relative pb-2">
        My Hobbies
        <span class="absolute bottom-0 left-0 w-3/5 h-1 bg-blue-600"></span>
      </h2>
      <div class="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div v-for="(hobby, index) in hobbies" :key="`hobby-${index}`"
          class="bg-white rounded-lg overflow-hidden shadow-md hover:shadow-lg transition-shadow duration-300">
          <img :src="hobby.imageUrl" :alt="hobby.name" class="w-full h-48 object-cover" />
          <div class="p-6">
            <h3 class="text-xl font-semibold text-blue-600 mb-2">{{ hobby.name }}</h3>
            <p class="text-gray-700">{{ hobby.description }}</p>
          </div>
        </div>
      </div>
    </div>

    <!-- Sports Section -->
    <div class="bg-white rounded-lg shadow-lg p-8 mb-12">
      <h2 class="text-3xl font-bold mb-8 relative pb-2">
        Cross Country Running
        <span class="absolute bottom-0 left-0 w-3/5 h-1 bg-blue-600"></span>
      </h2>
      <div class="flex flex-col md:flex-row-reverse gap-8 items-center">
        <div class="flex-1">
          <p class="text-gray-700 mb-6">
            [Describe your experience with cross country running. Talk about what you love about the sport,
            how long you've been running, and any notable achievements.]
          </p>
          <div class="achievements">
            <h3 class="text-2xl font-bold mb-4">Personal Records</h3>
            <ul class="list-none pl-0">
              <li v-for="(record, index) in runningRecords" :key="`record-${index}`" class="text-gray-700 mb-2">
                <strong>{{ record.distance }}:</strong> {{ record.time }}
              </li>
            </ul>
          </div>
        </div>
        <div class="flex-1 flex justify-center">
          <img :src="crossCountryImage" alt="Cross Country Running"
            class="w-full max-w-md h-64 object-cover rounded-lg shadow-md" />
        </div>
      </div>
    </div>

    <!-- Events Section -->
    <div class="bg-white rounded-lg shadow-lg p-8 mb-12">
      <h2 class="text-3xl font-bold mb-8 relative pb-2">
        Hackathons & Events
        <span class="absolute bottom-0 left-0 w-3/5 h-1 bg-blue-600"></span>
      </h2>
      <div class="relative pl-8">
        <div class="absolute left-8 top-0 bottom-0 w-1 bg-blue-100"></div>
        <div v-for="(event, index) in events" :key="`event-${index}`" class="mb-8 flex">
          <div class="w-32 pr-6 text-right font-bold text-blue-600">{{ event.date }}</div>
          <div class="flex-1 pl-6 relative">
            <div class="absolute -left-3 top-2 w-4 h-4 bg-blue-600 rounded-full"></div>
            <img :src="event.imageUrl" :alt="event.name" class="w-24 h-24 object-cover rounded-lg shadow-md mb-4" />
            <h3 class="text-xl font-bold text-blue-600 mb-2">{{ event.name }}</h3>
            <p class="text-gray-700 mb-4">{{ event.description }}</p>
            <div class="flex flex-wrap gap-2">
              <span v-for="(tag, tagIndex) in event.tags" :key="`tag-${tagIndex}`"
                class="px-3 py-1 bg-blue-100 text-blue-600 rounded-full text-sm">
                {{ tag }}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'AboutPage',
  data() {
    return {
      profileImage: 'https://via.placeholder.com/300x300?text=Profile+Picture',
      crossCountryImage: 'https://via.placeholder.com/400x250?text=Cross+Country',
      hobbies: [
        {
          name: 'Hobby 1',
          description: 'Description of your first hobby and why you enjoy it.',
          imageUrl: 'https://via.placeholder.com/300x200?text=Hobby+1'
        },
        {
          name: 'Hobby 2',
          description: 'Description of your second hobby and why you enjoy it.',
          imageUrl: 'https://via.placeholder.com/300x200?text=Hobby+2'
        },
        {
          name: 'Hobby 3',
          description: 'Description of your third hobby and why you enjoy it.',
          imageUrl: 'https://via.placeholder.com/300x200?text=Hobby+3'
        },
      ],
      runningRecords: [
        { distance: '5K', time: '00:00:00' },
        { distance: '10K', time: '00:00:00' },
        { distance: 'Half Marathon', time: '00:00:00' },
      ],
      events: [
        {
          name: 'Hackathon Name',
          date: 'Month Year',
          description: 'Description of the hackathon, your team, the project, and any accomplishments.',
          tags: ['JavaScript', 'Vue', 'AI'],
          imageUrl: 'https://via.placeholder.com/120x120?text=Event+1'
        },
        {
          name: 'Another Event',
          date: 'Month Year',
          description: 'Description of another event you participated in and what you learned.',
          tags: ['Design', 'Teamwork'],
          imageUrl: 'https://via.placeholder.com/120x120?text=Event+2'
        },
        {
          name: 'Competition',
          date: 'Month Year',
          description: 'Description of a competition or event you took part in.',
          tags: ['Algorithm', 'Problem Solving'],
          imageUrl: 'https://via.placeholder.com/120x120?text=Event+3'
        },
      ],
    };
  },
};
</script>